/*
  EZ video system: every video on the site opens in the same pop-out player.

  <ez-video-gallery> wraps a set of thumbnails ([data-ez-video]) and one
  player (snippets/ez-video-player.liquid). Clicking a thumbnail plays it
  in the player with previous/next through the rest of that gallery, so
  shoppers can compare models back to back. Thumbnails carry the video as
  data-video-id (YouTube) or data-video-src (a Shopify-hosted file), plus
  data-shape (wide | tall), data-title, data-price, data-save, data-url and
  data-cta for the text and button under the video.

  The iframe/video is only created when a video is opened and is removed
  on close, so nothing from YouTube loads until a shopper asks for it and
  playback stops when the player closes.

  Buttons outside a gallery (e.g. the product buy box) open a gallery's
  video with data-ez-video-target="<YouTube ID>"; if no gallery on the
  page has it, the button's own link (the video on YouTube) is followed.

  Also shows a row's scroll arrows when there are more cards than fit.
*/
(() => {
  if (customElements.get('ez-video-gallery')) return;

  const youtubeThumb = (id) => 'https://i.ytimg.com/vi/' + encodeURIComponent(id) + '/hqdefault.jpg';

  class EzVideoGallery extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-ez-video]'));
      this.setUpArrows();

      this.dialog = this.querySelector('.ez-video-pop');
      if (!this.dialog || typeof this.dialog.showModal !== 'function' || !this.items.length) return;

      const q = (selector) => this.dialog.querySelector(selector);
      this.panel = q('.ez-video-pop__panel');
      this.frame = q('[data-video-frame]');
      this.titleEl = q('[data-video-title]');
      this.priceRow = q('[data-video-price-row]');
      this.priceEl = q('[data-video-price]');
      this.saveEl = q('[data-video-save]');
      this.linkEl = q('[data-video-link]');
      this.ctaEl = q('[data-video-cta]');
      this.countEl = q('[data-video-count]');
      this.closeBtn = q('[data-video-close]');
      q('[data-video-nav]').hidden = this.items.length < 2;

      this.addEventListener('click', (event) => {
        const item = event.target.closest('[data-ez-video]');
        if (!item || !this.contains(item) || this.dialog.contains(item)) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        this.open(this.items.indexOf(item));
      });

      // Thumbnails that aren't links or buttons (e.g. the showroom clips) still need the keyboard
      this.items.forEach((item) => {
        if (item.matches('a[href], button')) return;
        item.setAttribute('role', 'button');
        item.setAttribute('tabindex', '0');
        item.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.open(this.items.indexOf(item));
        });
      });

      this.closeBtn.addEventListener('click', () => this.dialog.close());
      q('[data-video-prev]').addEventListener('click', () => this.step(-1));
      q('[data-video-next]').addEventListener('click', () => this.step(1));

      // Clicking the dimmed backdrop (the <dialog> itself, outside the panel) closes it.
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.dialog.close();
      });

      this.dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.step(-1);
        if (event.key === 'ArrowRight') this.step(1);
      });

      this.dialog.addEventListener('close', () => {
        this.frame.replaceChildren();
        this.frame.removeAttribute('data-loading');
        document.documentElement.classList.remove('ez-video-pop-open');
        const item = this.items[this.index];
        if (item && !this.openedFromOutside) item.focus({ preventScroll: true });
      });
    }

    openById(id) {
      const index = this.items.findIndex((item) => item.dataset.videoId === id);
      if (index === -1) return false;
      this.open(index, true);
      return true;
    }

    step(delta) {
      if (this.items.length < 2) return;
      const next = (this.index + delta + this.items.length) % this.items.length;
      this.open(next);
      // Keep the row in step so closing lands on the video just watched
      // (only our own scroll rows; Swiper sliders manage their own position)
      const card = this.items[next].closest('[data-strip-track] > li');
      if (card) card.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }

    open(index, fromOutside = false) {
      const item = this.items[index];
      if (!item) return;
      this.index = index;
      if (!this.dialog.open) this.openedFromOutside = fromOutside;

      const data = item.dataset;
      const title = data.title || '';

      this.dialog.classList.toggle('ez-video-pop--tall', data.shape === 'tall');
      this.panel.style.removeProperty('--ez-ar');
      this.frame.setAttribute('data-loading', '');

      let media;
      if (data.videoId) {
        // The thumbnail stands in while YouTube loads, instead of a black box
        this.frame.style.backgroundImage = 'url("' + youtubeThumb(data.videoId) + '")';
        media = document.createElement('iframe');
        media.src =
          'https://www.youtube-nocookie.com/embed/' +
          encodeURIComponent(data.videoId) +
          '?autoplay=1&rel=0&playsinline=1&modestbranding=1';
        media.title = title ? title + ' video' : 'Video';
        media.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        media.allowFullscreen = true;
        media.addEventListener('load', () => this.loaded(media));
      } else if (data.videoSrc) {
        this.frame.style.backgroundImage = data.poster ? 'url("' + data.poster + '")' : '';
        media = document.createElement('video');
        media.src = data.videoSrc;
        media.controls = true;
        media.autoplay = true;
        media.playsInline = true;
        media.addEventListener('loadedmetadata', () => {
          // Size the player to the clip (the showroom clips are 9:16 and 3:4)
          if (media.videoWidth && media.videoHeight) {
            this.panel.style.setProperty('--ez-ar', media.videoWidth + ' / ' + media.videoHeight);
          }
          this.loaded(media);
        });
        media.play().catch(() => {});
      } else {
        return;
      }
      this.frame.replaceChildren(media);

      this.titleEl.textContent = title;
      this.priceEl.textContent = data.price || '';
      this.saveEl.textContent = data.save || '';
      this.priceRow.hidden = !data.price && !data.save;
      if (data.url) {
        this.linkEl.href = data.url;
        this.ctaEl.textContent = data.cta || 'View product';
        this.linkEl.hidden = false;
        if (/^https?:/.test(data.url) && !data.url.startsWith(location.origin)) {
          this.linkEl.target = '_blank';
          this.linkEl.rel = 'noopener';
        } else {
          this.linkEl.removeAttribute('target');
          this.linkEl.removeAttribute('rel');
        }
      } else {
        this.linkEl.hidden = true;
      }
      this.countEl.textContent = index + 1 + ' / ' + this.items.length;
      this.dialog.setAttribute('aria-label', title ? title + ' video' : 'Video');

      if (!this.dialog.open) {
        document.documentElement.classList.add('ez-video-pop-open');
        this.dialog.showModal();
        this.closeBtn.focus();
      }
    }

    loaded(media) {
      media.classList.add('is-loaded');
      if (this.frame.contains(media)) this.frame.removeAttribute('data-loading');
    }

    setUpArrows() {
      const track = this.querySelector('[data-strip-track]');
      const arrows = this.querySelector('[data-strip-arrows]');
      if (!track || !arrows) return;
      const prev = arrows.querySelector('[data-strip-prev]');
      const next = arrows.querySelector('[data-strip-next]');

      const update = () => {
        const max = track.scrollWidth - track.clientWidth;
        arrows.hidden = max <= 1;
        prev.disabled = track.scrollLeft <= 1;
        next.disabled = track.scrollLeft >= max - 1;
      };

      const page = (direction) => {
        track.scrollBy({ left: direction * track.clientWidth * 0.9, behavior: 'smooth' });
      };

      prev.addEventListener('click', () => page(-1));
      next.addEventListener('click', () => page(1));
      track.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
      update();
    }
  }

  customElements.define('ez-video-gallery', EzVideoGallery);

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-ez-video-target]');
    if (!trigger || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const id = trigger.dataset.ezVideoTarget;
    for (const gallery of document.querySelectorAll('ez-video-gallery')) {
      if (typeof gallery.openById === 'function' && gallery.dialog && gallery.openById(id)) {
        event.preventDefault();
        return;
      }
    }
  });
})();
